/* eslint-disable @typescript-eslint/no-unused-vars */
import { AuthUser } from 'auth-provider/require-auth';
import Slider from 'components/Slider';
import { Badge } from 'components/ui/badge';
import { Button } from 'components/ui/button';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { Flame } from 'lucide-react';
import { useNavigate } from 'react-router';
import { Fragment } from 'react/jsx-runtime';
import { usePostsStore } from 'stores/post-store';
import { cn } from 'utils/cn';
import { toastConfig } from 'utils/toast.config';
import { urlConfig } from 'utils/url-config';
import useGetPosts from './hooks/use-get-posts';
import { useEffect } from 'react';
import { isVideoLink } from 'utils/ckeck-video';

dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

function formatAddress(address: string, neighborhood: string) {
	const formattedAddress = address
		.replace(/[^\w\s\\/]/g, '')
		.replace(/[\s\\/]+/g, '-')
		.toLowerCase();
	return neighborhood ? `${formattedAddress}-${neighborhood.toLowerCase()}` : `${formattedAddress}`;
}

const postDealTypeColor: (dealType: string) => string = (dealType: string) => {
	switch (dealType) {
		case 'rent':
			return 'bg-[#131415] hover:bg-[#131415]/80';
		case 'sale':
			return 'bg-[#131415] hover:bg-[#131415]/80';
		case 'daily':
			return 'bg-[#131415] hover:bg-[#131415]/80';
		default:
			return 'bg-[#131415] hover:bg-[#131415]/80';
	}
};

const Post = ({ post, sliderClasses, postClasses, auth, scrollRef, ref }: { post: any; sliderClasses?: string; postClasses?: string; auth?: AuthUser | null; scrollRef?: any; ref?: any  }) => {
	const setPostId = usePostsStore((state) => state.setPostId);
	const navigate = useNavigate();
	const path = window.location.pathname.includes('post');
	const { refetch } = useGetPosts();
	const createdAt = dayjs(post.created_at);

	const today = dayjs().startOf('day');
	const dayBeforeYesterday = dayjs().subtract(2, 'day').startOf('day');
	const handleSaveToPost = () => {
		const safeToPostIds = JSON.parse(localStorage.getItem('safeToPostIds') || '[]');
		const index = safeToPostIds.indexOf(post.post_id);

		if (index !== -1) {
			const updatedSafeToPostIds = [...safeToPostIds.slice(0, index), ...safeToPostIds.slice(index + 1)];
			localStorage.setItem('safeToPostIds', JSON.stringify(updatedSafeToPostIds));
			refetch();
			toastConfig.favouritePost.delete.error();
		} else {
			const updatedSafeToPostIds = [...safeToPostIds, post.post_id];
			localStorage.setItem('safeToPostIds', JSON.stringify(updatedSafeToPostIds));
			refetch();
			toastConfig.favouritePost.add.success();
		}
	};

	const safeToPostIdsString = localStorage.getItem('safeToPostIds');

	const safeToPostIds = safeToPostIdsString ? JSON.parse(safeToPostIdsString) : [];

	return (
		<div className={cn('flex  gap-5 p-2 overflow-x-auto relative', postClasses)} ref={scrollRef}>
			{post.photos.some((item: any) => isVideoLink(item.photo as string)) && (
					<svg className="flex absolute p-1 border-none rounded-2xl w-8 m-2 bg-primary/90 left-2 top-2 z-[50]" width="24" height="24" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
						<mask id="mask0_206_223" maskUnits="userSpaceOnUse" x="0" y="0" width="24" height="24">
							<rect width="24" height="24" fill="#D9D9D9" />
						</mask>
						<g mask="url(#mask0_206_223)">
							<path
								d="M2.66667 13.3333C2.3 13.3333 1.98611 13.2027 1.725 12.9416C1.46389 12.6805 1.33333 12.3666 1.33333 12V3.99996C1.33333 3.63329 1.46389 3.3194 1.725 3.05829C1.98611 2.79718 2.3 2.66663 2.66667 2.66663H10.6667C11.0333 2.66663 11.3472 2.79718 11.6083 3.05829C11.8694 3.3194 12 3.63329 12 3.99996V6.99996L14.6667 4.33329V11.6666L12 8.99996V12C12 12.3666 11.8694 12.6805 11.6083 12.9416C11.3472 13.2027 11.0333 13.3333 10.6667 13.3333H2.66667ZM2.66667 12H10.6667V3.99996H2.66667V12Z"
								fill="white"
							/>
						</g>
					</svg>
				)}
			<button
				onClick={handleSaveToPost}
				className="font-bebas text-3xl z-[50] flex justify-end absolute top-3 right-3" // Added positioning styles
			>
				{safeToPostIds.includes(post.post_id) ? (
					<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
						<mask id="mask0_199_1166" maskUnits="userSpaceOnUse" x="0" y="0" width="24" height="24">
							<rect width="24" height="24" fill="#D9D9D9" />
						</mask>
						<g mask="url(#mask0_199_1166)">
							<path d="M7.5 4C9.1 4 10.8333 5 11.5 5.5L15 3C16.3333 3.33333 19.3 4.3 20.5 5.5C21.7 6.7 21 9.33333 20.5 10.5L12 19L3.5 11L4 5.5C4.5 5 5.9 4 7.5 4Z" fill="#131415" />
							<path
								d="M12 21L10.55 19.7C8.86667 18.1834 7.475 16.875 6.375 15.775C5.275 14.675 4.4 13.6875 3.75 12.8125C3.1 11.9375 2.64583 11.1334 2.3875 10.4C2.12917 9.66669 2 8.91669 2 8.15002C2 6.58336 2.525 5.27502 3.575 4.22502C4.625 3.17502 5.93333 2.65002 7.5 2.65002C8.36667 2.65002 9.19167 2.83336 9.975 3.20002C10.7583 3.56669 11.4333 4.08336 12 4.75002C12.5667 4.08336 13.2417 3.56669 14.025 3.20002C14.8083 2.83336 15.6333 2.65002 16.5 2.65002C18.0667 2.65002 19.375 3.17502 20.425 4.22502C21.475 5.27502 22 6.58336 22 8.15002C22 8.91669 21.8708 9.66669 21.6125 10.4C21.3542 11.1334 20.9 11.9375 20.25 12.8125C19.6 13.6875 18.725 14.675 17.625 15.775C16.525 16.875 15.1333 18.1834 13.45 19.7L12 21ZM12 18.3C13.6 16.8667 14.9167 15.6375 15.95 14.6125C16.9833 13.5875 17.8 12.6959 18.4 11.9375C19 11.1792 19.4167 10.5042 19.65 9.91252C19.8833 9.32086 20 8.73336 20 8.15002C20 7.15002 19.6667 6.31669 19 5.65002C18.3333 4.98336 17.5 4.65002 16.5 4.65002C15.7167 4.65002 14.9917 4.87086 14.325 5.31252C13.6583 5.75419 13.2 6.31669 12.95 7.00002H11.05C10.8 6.31669 10.3417 5.75419 9.675 5.31252C9.00833 4.87086 8.28333 4.65002 7.5 4.65002C6.5 4.65002 5.66667 4.98336 5 5.65002C4.33333 6.31669 4 7.15002 4 8.15002C4 8.73336 4.11667 9.32086 4.35 9.91252C4.58333 10.5042 5 11.1792 5.6 11.9375C6.2 12.6959 7.01667 13.5875 8.05 14.6125C9.08333 15.6375 10.4 16.8667 12 18.3Z"
								fill="#131415"
							/>
						</g>
					</svg>
				) : (
					<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
						<mask id="mask0_199_1165" maskUnits="userSpaceOnUse" x="0" y="0" width="24" height="24">
							<rect width="24" height="24" fill="#D9D9D9" />
						</mask>
						<g mask="url(#mask0_199_1165)">
							<path
								d="M12 21L10.55 19.7C8.86667 18.1834 7.475 16.875 6.375 15.775C5.275 14.675 4.4 13.6875 3.75 12.8125C3.1 11.9375 2.64583 11.1334 2.3875 10.4C2.12917 9.66669 2 8.91669 2 8.15002C2 6.58336 2.525 5.27502 3.575 4.22502C4.625 3.17502 5.93333 2.65002 7.5 2.65002C8.36667 2.65002 9.19167 2.83336 9.975 3.20002C10.7583 3.56669 11.4333 4.08336 12 4.75002C12.5667 4.08336 13.2417 3.56669 14.025 3.20002C14.8083 2.83336 15.6333 2.65002 16.5 2.65002C18.0667 2.65002 19.375 3.17502 20.425 4.22502C21.475 5.27502 22 6.58336 22 8.15002C22 8.91669 21.8708 9.66669 21.6125 10.4C21.3542 11.1334 20.9 11.9375 20.25 12.8125C19.6 13.6875 18.725 14.675 17.625 15.775C16.525 16.875 15.1333 18.1834 13.45 19.7L12 21ZM12 18.3C13.6 16.8667 14.9167 15.6375 15.95 14.6125C16.9833 13.5875 17.8 12.6959 18.4 11.9375C19 11.1792 19.4167 10.5042 19.65 9.91252C19.8833 9.32086 20 8.73336 20 8.15002C20 7.15002 19.6667 6.31669 19 5.65002C18.3333 4.98336 17.5 4.65002 16.5 4.65002C15.7167 4.65002 14.9917 4.87086 14.325 5.31252C13.6583 5.75419 13.2 6.31669 12.95 7.00002H11.05C10.8 6.31669 10.3417 5.75419 9.675 5.31252C9.00833 4.87086 8.28333 4.65002 7.5 4.65002C6.5 4.65002 5.66667 4.98336 5 5.65002C4.33333 6.31669 4 7.15002 4 8.15002C4 8.73336 4.11667 9.32086 4.35 9.91252C4.58333 10.5042 5 11.1792 5.6 11.9375C6.2 12.6959 7.01667 13.5875 8.05 14.6125C9.08333 15.6375 10.4 16.8667 12 18.3Z"
								fill="#131415"
							/>
						</g>
					</svg>
				)}
			</button>
			<Slider data={post.photos} className={cn('', sliderClasses)} />
			<div onClick={() => setPostId(post.post_id)} className="flex flex-col ">
				
				<div className="flex items-center justify-between">
					<span className="font-bebas text-3xl relative">${post.post_price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')}</span>

					{path && (
						<Badge variant="outline" className={cn('w-fit text-base', 'flex items-center gap-1', 'capitalize')} onClick={() => navigate(urlConfig.pages.post.replace(':id', post.post_id))}>
							{post.post_deal_type}
						</Badge>
					)}
				</div>
				{path && (
					<div className="flex gap-1 text-sm font-light break-all">
						<span>${post.post_deposit} Deposit</span>
						<span>•</span>
						<span>{post.post_commission}% commission</span>
					</div>
				)}
				{!path && (
					<div className="flex gap-1 text-sm font-light break-all">
						<span>${post.post_deposit} Deposit</span>
						<span>•</span>
						<span>{post.post_commission}% commission</span>
					</div>
				)}
				<div className="flex gap-1 items-center mt-2">
					<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
						<path
							d="M10 20C8.61667 20 7.31667 19.7375 6.1 19.2125C4.88333 18.6875 3.825 17.975 2.925 17.075C2.025 16.175 1.3125 15.1167 0.7875 13.9C0.2625 12.6833 0 11.3833 0 10C0 8.61667 0.2625 7.31667 0.7875 6.1C1.3125 4.88333 2.025 3.825 2.925 2.925C3.825 2.025 4.88333 1.3125 6.1 0.7875C7.31667 0.2625 8.61667 0 10 0C11.3833 0 12.6833 0.2625 13.9 0.7875C15.1167 1.3125 16.175 2.025 17.075 2.925C17.975 3.825 18.6875 4.88333 19.2125 6.1C19.7375 7.31667 20 8.61667 20 10C20 11.3833 19.7375 12.6833 19.2125 13.9C18.6875 15.1167 17.975 16.175 17.075 17.075C16.175 17.975 15.1167 18.6875 13.9 19.2125C12.6833 19.7375 11.3833 20 10 20ZM10 15.5C10.75 14.75 11.4167 13.975 12 13.175C12.5 12.4917 12.9583 11.7417 13.375 10.925C13.7917 10.1083 14 9.3 14 8.5C14 7.4 13.6083 6.45833 12.825 5.675C12.0417 4.89167 11.1 4.5 10 4.5C8.9 4.5 7.95833 4.89167 7.175 5.675C6.39167 6.45833 6 7.4 6 8.5C6 9.3 6.20833 10.1083 6.625 10.925C7.04167 11.7417 7.5 12.4917 8 13.175C8.58333 13.975 9.25 14.75 10 15.5ZM10 10C9.58333 10 9.22917 9.85417 8.9375 9.5625C8.64583 9.27083 8.5 8.91667 8.5 8.5C8.5 8.08333 8.64583 7.72917 8.9375 7.4375C9.22917 7.14583 9.58333 7 10 7C10.4167 7 10.7708 7.14583 11.0625 7.4375C11.3542 7.72917 11.5 8.08333 11.5 8.5C11.5 8.91667 11.3542 9.27083 11.0625 9.5625C10.7708 9.85417 10.4167 10 10 10Z"
							fill="#1C1B1F"
						/>
					</svg>
					<span className="text-wrap break-all">{post.post_address}</span>
				</div>
				<div className="flex items-center gap-1 mt-1">
					<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
						<circle cx="10" cy="10" r="10" fill="#131415" />
						<path
							d="M8.98574 14.334L6.24847 8.76171V6H7.01833L10.1833 12.4888H9.8778L12.8961 6H13.0794V8.51731L10.3544 14.334H8.98574ZM4 14.334V13.3442H5.00204V6.98982H4.02444V6H6.24847V13.3442H7.33605V14.334H4ZM11.9796 14.334V13.3442H13.0794V6H15.9756V6.98982H14.998V13.3442H16V14.334H11.9796Z"
							fill="white"
						/>
					</svg>
					<span className="text-wrap break-all">{post.post_metro_station}</span>
				</div>
				<div className="flex items-center gap-2 mt-2 flex-wrap">
					<div className="flex items-center gap-1">
						<svg width="18" height="13" viewBox="0 0 18 13" fill="none" xmlns="http://www.w3.org/2000/svg">
							<path d="M6.55001 13L0.850006 7.29998L2.27501 5.87498L6.55001 10.15L15.725 0.974976L17.15 2.39998L6.55001 13Z" fill="#131415" />
						</svg>
						<span className="text-wrap break-all">
							{post.post_rooms} room{post.post_rooms > 1 && 's'}
						</span>
					</div>
					<div className="flex items-center gap-1">
						<svg width="18" height="13" viewBox="0 0 18 13" fill="none" xmlns="http://www.w3.org/2000/svg">
							<path d="M6.55001 13L0.850006 7.29998L2.27501 5.87498L6.55001 10.15L15.725 0.974976L17.15 2.39998L6.55001 13Z" fill="#131415" />
						</svg>
						<span className="text-wrap break-all">{post.post_sq_meters} sq.m</span>
					</div>
					<div className="flex items-center gap-1">
						<svg width="18" height="13" viewBox="0 0 18 13" fill="none" xmlns="http://www.w3.org/2000/svg">
							<path d="M6.55001 13L0.850006 7.29998L2.27501 5.87498L6.55001 10.15L15.725 0.974976L17.15 2.39998L6.55001 13Z" fill="#131415" />
						</svg>
						<span className="text-wrap break-all">
							{post.post_bedroom} bedroom{post.post_bedroom > 1 && 's'}
						</span>
					</div>
					{post?.post_floor && path && (
						<div className="flex items-center gap-1">
							<svg width="18" height="13" viewBox="0 0 18 13" fill="none" xmlns="http://www.w3.org/2000/svg">
								<path d="M6.55001 13L0.850006 7.29998L2.27501 5.87498L6.55001 10.15L15.725 0.974976L17.15 2.39998L6.55001 13Z" fill="#131415" />
							</svg>
							<span className="text-wrap break-all">{post.post_floor} floor</span>
						</div>
					)}
					{post.post_tags && path && JSON.parse(post.post_tags).tags.length > 0 && (
						<Fragment key={post.post_id}>
							{post.post_tags &&
								JSON.parse(post?.post_tags)?.tags?.map((tag: string, index: number) => (
									<div className="flex items-center gap-1" key={index}>
										<svg width="18" height="13" viewBox="0 0 18 13" fill="none" xmlns="http://www.w3.org/2000/svg">
											<path d="M6.55001 13L0.850006 7.29998L2.27501 5.87498L6.55001 10.15L15.725 0.974976L17.15 2.39998L6.55001 13Z" fill="#131415" />
										</svg>
										<span className="text-wrap break-all">{tag}</span>
									</div>
								))}
						</Fragment>
					)}
				</div>
				{path && (
					<div className="mt-2 flex items-center gap-2">
						{post?.post_tenants_max && (
							<div className="flex items-center gap-1">
								<svg width="22" height="16" viewBox="0 0 22 16" fill="none" xmlns="http://www.w3.org/2000/svg">
									<path
										d="M0 16V13.2C0 12.6333 0.145833 12.1125 0.4375 11.6375C0.729167 11.1625 1.11667 10.8 1.6 10.55C2.63333 10.0333 3.68333 9.64583 4.75 9.3875C5.81667 9.12917 6.9 9 8 9C9.1 9 10.1833 9.12917 11.25 9.3875C12.3167 9.64583 13.3667 10.0333 14.4 10.55C14.8833 10.8 15.2708 11.1625 15.5625 11.6375C15.8542 12.1125 16 12.6333 16 13.2V16H0ZM18 16V13C18 12.2667 17.7958 11.5625 17.3875 10.8875C16.9792 10.2125 16.4 9.63333 15.65 9.15C16.5 9.25 17.3 9.42083 18.05 9.6625C18.8 9.90417 19.5 10.2 20.15 10.55C20.75 10.8833 21.2083 11.2542 21.525 11.6625C21.8417 12.0708 22 12.5167 22 13V16H18ZM8 8C6.9 8 5.95833 7.60833 5.175 6.825C4.39167 6.04167 4 5.1 4 4C4 2.9 4.39167 1.95833 5.175 1.175C5.95833 0.391667 6.9 0 8 0C9.1 0 10.0417 0.391667 10.825 1.175C11.6083 1.95833 12 2.9 12 4C12 5.1 11.6083 6.04167 10.825 6.825C10.0417 7.60833 9.1 8 8 8ZM18 4C18 5.1 17.6083 6.04167 16.825 6.825C16.0417 7.60833 15.1 8 14 8C13.8167 8 13.5833 7.97917 13.3 7.9375C13.0167 7.89583 12.7833 7.85 12.6 7.8C13.05 7.26667 13.3958 6.675 13.6375 6.025C13.8792 5.375 14 4.7 14 4C14 3.3 13.8792 2.625 13.6375 1.975C13.3958 1.325 13.05 0.733333 12.6 0.2C12.8333 0.116667 13.0667 0.0625 13.3 0.0375C13.5333 0.0125 13.7667 0 14 0C15.1 0 16.0417 0.391667 16.825 1.175C17.6083 1.95833 18 2.9 18 4ZM2 14H14V13.2C14 13.0167 13.9542 12.85 13.8625 12.7C13.7708 12.55 13.65 12.4333 13.5 12.35C12.6 11.9 11.6917 11.5625 10.775 11.3375C9.85833 11.1125 8.93333 11 8 11C7.06667 11 6.14167 11.1125 5.225 11.3375C4.30833 11.5625 3.4 11.9 2.5 12.35C2.35 12.4333 2.22917 12.55 2.1375 12.7C2.04583 12.85 2 13.0167 2 13.2V14ZM8 6C8.55 6 9.02083 5.80417 9.4125 5.4125C9.80417 5.02083 10 4.55 10 4C10 3.45 9.80417 2.97917 9.4125 2.5875C9.02083 2.19583 8.55 2 8 2C7.45 2 6.97917 2.19583 6.5875 2.5875C6.19583 2.97917 6 3.45 6 4C6 4.55 6.19583 5.02083 6.5875 5.4125C6.97917 5.80417 7.45 6 8 6Z"
										fill="#131415"
									/>
								</svg>
								<span className="text-wrap break-all">Tenants: {post.post_tenants_max > 1 ? `1-${post.post_tenants_max}` : '1'}</span>
							</div>
						)}
						{post?.post_pets && (
							<div className="flex items-center gap-1">
								<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
									<path
										d="M2.5 9.125C1.8 9.125 1.20833 8.88333 0.725 8.4C0.241667 7.91667 0 7.325 0 6.625C0 5.925 0.241667 5.33333 0.725 4.85C1.20833 4.36667 1.8 4.125 2.5 4.125C3.2 4.125 3.79167 4.36667 4.275 4.85C4.75833 5.33333 5 5.925 5 6.625C5 7.325 4.75833 7.91667 4.275 8.4C3.79167 8.88333 3.2 9.125 2.5 9.125ZM7 5.125C6.3 5.125 5.70833 4.88333 5.225 4.4C4.74167 3.91667 4.5 3.325 4.5 2.625C4.5 1.925 4.74167 1.33333 5.225 0.85C5.70833 0.366667 6.3 0.125 7 0.125C7.7 0.125 8.29167 0.366667 8.775 0.85C9.25833 1.33333 9.5 1.925 9.5 2.625C9.5 3.325 9.25833 3.91667 8.775 4.4C8.29167 4.88333 7.7 5.125 7 5.125ZM13 5.125C12.3 5.125 11.7083 4.88333 11.225 4.4C10.7417 3.91667 10.5 3.325 10.5 2.625C10.5 1.925 10.7417 1.33333 11.225 0.85C11.7083 0.366667 12.3 0.125 13 0.125C13.7 0.125 14.2917 0.366667 14.775 0.85C15.2583 1.33333 15.5 1.925 15.5 2.625C15.5 3.325 15.2583 3.91667 14.775 4.4C14.2917 4.88333 13.7 5.125 13 5.125ZM17.5 9.125C16.8 9.125 16.2083 8.88333 15.725 8.4C15.2417 7.91667 15 7.325 15 6.625C15 5.925 15.2417 5.33333 15.725 4.85C16.2083 4.36667 16.8 4.125 17.5 4.125C18.2 4.125 18.7917 4.36667 19.275 4.85C19.7583 5.33333 20 5.925 20 6.625C20 7.325 19.7583 7.91667 19.275 8.4C18.7917 8.88333 18.2 9.125 17.5 9.125ZM4.65 19.125C3.9 19.125 3.27083 18.8375 2.7625 18.2625C2.25417 17.6875 2 17.0083 2 16.225C2 15.3583 2.29583 14.6 2.8875 13.95C3.47917 13.3 4.06667 12.6583 4.65 12.025C5.13333 11.5083 5.55 10.9458 5.9 10.3375C6.25 9.72917 6.66667 9.15833 7.15 8.625C7.51667 8.19167 7.94167 7.83333 8.425 7.55C8.90833 7.26667 9.43333 7.125 10 7.125C10.5667 7.125 11.0917 7.25833 11.575 7.525C12.0583 7.79167 12.4833 8.14167 12.85 8.575C13.3167 9.10833 13.7292 9.68333 14.0875 10.3C14.4458 10.9167 14.8667 11.4917 15.35 12.025C15.9333 12.6583 16.5208 13.3 17.1125 13.95C17.7042 14.6 18 15.3583 18 16.225C18 17.0083 17.7458 17.6875 17.2375 18.2625C16.7292 18.8375 16.1 19.125 15.35 19.125C14.45 19.125 13.5583 19.05 12.675 18.9C11.7917 18.75 10.9 18.675 10 18.675C9.1 18.675 8.20833 18.75 7.325 18.9C6.44167 19.05 5.55 19.125 4.65 19.125Z"
										fill="#131415"
									/>
								</svg>
								<span className="text-wrap break-all">Pets: {post.post_pets}</span>
							</div>
						)}
					</div>
				)}
				{!path && (
					<Button
						className={cn('w-full md:w-fit mt-2 md:mt-[10%]')}
						onClick={() => {
							if (post.post_address) {
								navigate(urlConfig.pages.postWithAddress.replace(':id/:address', `${post.post_id}/${formatAddress(post.post_address, post.post_neighborhood)}`));
							} else {
								navigate(urlConfig.pages.post.replace(':id', post.post_id));
							}
							setPostId(post.post_id);
						}}>
						Open details
					</Button>
				)}

				{!path && !post.is_approved && (
					<Badge variant="destructive" className="w-fit absolute z-20 top-4 left-4" onClick={() => navigate(urlConfig.pages.post.replace(':id', post.post_id))}>
						Need to approve
					</Badge>
				)}
			</div>
		</div>
	);
};
export default Post;
